// services/master-data-api.service.ts
import { District, Mandal } from '../types/territorial.types';
import { getAuthHeaders } from '../utils/authHeaders';
import {
   
    WorkTypeEntity,
    Contractor,
    DistrictWithStatsDto,
    MandalWithStatsDto,
    ContractorWithStatsDto,
    SearchResultsDto,
    MasterDataStatsDto,
    HealthCheckDto,
    ExportDataDto,
    DropdownValuesDto,
    ValidationResponseDto,
    SeedProgressDto,
    DistrictMandalSeedDto,
    SearchParams,
    FilterParams,
    ApiResponse
  } from '../types/master-data.types';
  
  export class MasterDataAPIService {
    private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  
    private baseURL: string;
  
    constructor(baseURL: string = process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1') {
      this.baseURL = baseURL;
    }
  
    // Generic HTTP methods
    private async request<T>(
      endpoint: string, 
      options: RequestInit = {}
    ): Promise<T> {
      const url = `${this.baseURL}${endpoint}`;
      
      const defaultOptions: RequestInit = {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
          ...options.headers,
        },
      };

      const response = await fetch(url, { ...options, ...defaultOptions });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API Error: ${response.status} ${response.statusText}`);
      }
  
      return response.json();
    }
  
    private async get<T>(endpoint: string): Promise<T> {
      return this.request<T>(endpoint, { method: 'GET' });
    }
  
    private async post<T>(endpoint: string, data?: any): Promise<T> {
      return this.request<T>(endpoint, {
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
      });
    }

    private async put<T>(endpoint: string, data?: any): Promise<T> {
      return this.request<T>(endpoint, {
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined,
      });
    }

    private async delete<T>(endpoint: string): Promise<T> {
      return this.request<T>(endpoint, { method: 'DELETE' });
    }
  
    // ==========================================
    // DISTRICT ENDPOINTS
    // ==========================================
  
    /**
     * Get all active districts
     */
    async getDistricts(): Promise<District[]> {
      return this.get<District[]>('/master-data/districts');
    }
  
    /**
     * Get districts with statistics
     */
    async getDistrictsWithStats(): Promise<DistrictWithStatsDto[]> {
      return this.get<DistrictWithStatsDto[]>('/master-data/districts/stats');
    }
  
    /**
     * Get district by ID
     */
    async getDistrictById(districtId: number): Promise<District> {
      return this.get<District>(`/master-data/districts/${districtId}`);
    }
  

    async getCached<T>(key: string, fetcher: () => Promise<T>, ttl: number = this.DEFAULT_TTL): Promise<T> {
      const cached = this.cache.get(key);
      
      if (cached && Date.now() - cached.timestamp < cached.ttl) {
        return cached.data;
      }
      
      const data = await fetcher();
      this.cache.set(key, { data, timestamp: Date.now(), ttl });
      
      return data;
    }

    clearCache(pattern?: string): void {
      if (pattern) {
        const keysToDelete: string[] = [];
        this.cache.forEach((_, key) => {
          if (key.includes(pattern)) {
            keysToDelete.push(key);
          }
        });
        keysToDelete.forEach(key => this.cache.delete(key));
      } else {
        this.cache.clear();
      }
    }
    // ==========================================
    // MANDAL ENDPOINTS
    // ==========================================
  
    /**
     * Get all mandals or filter by district
     */
    async getMandals(districtId?: number): Promise<Mandal[]> {
      const query = districtId ? `?districtId=${districtId}` : '';
      return this.get<Mandal[]>(`/master-data/mandals${query}`);
    }
  
    /**
     * Get mandals by district
     */
    async getMandalsByDistrict(districtId: number): Promise<Mandal[]> {
      return this.get<Mandal[]>(`/master-data/districts/${districtId}/mandals`);
    }
  
    /**
     * Get mandals with statistics
     */
    async getMandalsWithStats(districtId?: number): Promise<MandalWithStatsDto[]> {
      const query = districtId ? `?districtId=${districtId}` : '';
      return this.get<MandalWithStatsDto[]>(`/master-data/mandals/stats${query}`);
    }
  
    // ==========================================
    // WORK TYPE ENDPOINTS
    // ==========================================
  
    /**
     * Get all work types or filter by category
     */
    async getWorkTypes(category?: string): Promise<WorkTypeEntity[]> {
      const query = category ? `?category=${encodeURIComponent(category)}` : '';
      return this.get<WorkTypeEntity[]>(`/master-data/work-types${query}`);
    }
  
    /**
     * Get all work type categories
     */
    async getWorkTypeCategories(): Promise<string[]> {
      return this.get<string[]>('/master-data/work-types/categories');
    }
  
    // ==========================================
    // CONTRACTOR ENDPOINTS
    // ==========================================
  
    /**
     * Get all active contractors
     */
    async getContractors(): Promise<Contractor[]> {
      return this.get<Contractor[]>('/master-data/contractors');
    }
  
    /**
     * Get contractors with statistics
     */
    async getContractorsWithStats(): Promise<ContractorWithStatsDto[]> {
      return this.get<ContractorWithStatsDto[]>('/master-data/contractors/stats');
    }
  
    /**
     * Get contractor by ID
     */
    async getContractorById(contractorId: number): Promise<Contractor> {
      return this.get<Contractor>(`/master-data/contractors/${contractorId}`);
    }

    /**
     * Create a contractor / vendor / agency
     */
    async createContractor(data: Partial<Contractor>): Promise<Contractor> {
      return this.post<Contractor>('/master-data/contractors', data);
    }

    /**
     * Update a contractor / vendor / agency
     */
    async updateContractor(contractorId: number, data: Partial<Contractor>): Promise<Contractor> {
      return this.put<Contractor>(`/master-data/contractors/${contractorId}`, data);
    }

    /**
     * Deactivate (soft-delete) a contractor / vendor / agency
     */
    async deleteContractor(contractorId: number): Promise<{ success: boolean; message: string }> {
      return this.delete<{ success: boolean; message: string }>(`/master-data/contractors/${contractorId}`);
    }

    // ==========================================
    // SEARCH ENDPOINTS
    // ==========================================
  
    /**
     * Search across all master data
     */
    async searchMasterData(params: SearchParams): Promise<SearchResultsDto> {
      const queryParams = new URLSearchParams();
      queryParams.append('term', params.term);
      
      if (params.category) {
        queryParams.append('category', params.category);
      }
      if (params.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params.limit) {
        queryParams.append('limit', params.limit.toString());
      }
  
      return this.get<SearchResultsDto>(`/search?${queryParams.toString()}`);
    }
  
    // ==========================================
    // VALIDATION ENDPOINTS
    // ==========================================
  
    /**
     * Validate district-mandal combination
     */
    async validateDistrictMandalCombination(
      districtId: number, 
      mandalId: number
    ): Promise<ValidationResponseDto> {
      return this.get<ValidationResponseDto>(`/master-data/validate/district-mandal/${districtId}/${mandalId}`);
    }
  
    /**
     * Validate if contractor is active
     */
    async validateContractorActive(contractorId: number): Promise<ValidationResponseDto> {
      return this.get<ValidationResponseDto>(`/master-data/validate/contractor/${contractorId}`);
    }
  
    // ==========================================
    // UTILITY ENDPOINTS
    // ==========================================
  
    /**
     * Get dropdown values for forms
     */
    async getDropdownValues(): Promise<DropdownValuesDto> {
      return this.get<DropdownValuesDto>('/master-data/dropdowns');
    }
  
    /**
     * Get comprehensive master data statistics
     */
    async getMasterDataStats(): Promise<MasterDataStatsDto> {
      return this.get<MasterDataStatsDto>('/master-data/stats');
    }
  
    /**
     * Export all master data
     */
    async exportMasterData(): Promise<ExportDataDto> {
      return this.get<ExportDataDto>('/master-data/export');
    }
  
    /**
     * Health check for master data service
     */
    async healthCheck(): Promise<HealthCheckDto> {
      return this.get<HealthCheckDto>('/master-data/health');
    }
  
    // ==========================================
    // SEED DATA ENDPOINTS
    // ==========================================
  
    /**
     * Seed master data
     */
    async seedMasterData(): Promise<ApiResponse<SeedProgressDto>> {
      return this.post<ApiResponse<SeedProgressDto>>('/master-data/seed');
    }
  
    /**
     * Seed mandal data for districts
     */
    async seedMandalData(
      seedData?: DistrictMandalSeedDto[]
    ): Promise<ApiResponse<{ created: number; updated: number; errors: number }>> {
      return this.post<ApiResponse<{ created: number; updated: number; errors: number }>>(
        '/master-data/seed/mandals', 
        seedData
      );
    }
  
    // ==========================================
    // CONVENIENCE METHODS
    // ==========================================
  
    /**
     * Get filtered mandals with advanced options
     */
    async getFilteredMandals(filters: FilterParams): Promise<Mandal[]> {
      const queryParams = new URLSearchParams();
      
      if (filters.districtId) {
        queryParams.append('districtId', filters.districtId.toString());
      }
      if (filters.isActive !== undefined) {
        queryParams.append('isActive', filters.isActive.toString());
      }
      if (filters.page) {
        queryParams.append('page', filters.page.toString());
      }
      if (filters.limit) {
        queryParams.append('limit', filters.limit.toString());
      }
  
      return this.get<Mandal[]>(`/master-data/mandals?${queryParams.toString()}`);
    }
  
    /**
     * Get filtered work types with advanced options
     */
    async getFilteredWorkTypes(filters: FilterParams): Promise<WorkTypeEntity[]> {
      const queryParams = new URLSearchParams();
      
      if (filters.category) {
        queryParams.append('category', filters.category);
      }
      if (filters.isActive !== undefined) {
        queryParams.append('isActive', filters.isActive.toString());
      }
      if (filters.page) {
        queryParams.append('page', filters.page.toString());
      }
      if (filters.limit) {
        queryParams.append('limit', filters.limit.toString());
      }
  
      return this.get<WorkTypeEntity[]>(`/master-data/work-types?${queryParams.toString()}`);
    }
  
    /**
     * Bulk validate district-mandal combinations
     */
    async bulkValidateDistrictMandal(
      combinations: Array<{ districtId: number; mandalId: number }>
    ): Promise<ValidationResponseDto[]> {
      const validationPromises = combinations.map(({ districtId, mandalId }) =>
        this.validateDistrictMandalCombination(districtId, mandalId)
      );
      
      return Promise.all(validationPromises);
    }
  
    /**
     * Get complete master data summary
     */
    async getMasterDataSummary(): Promise<{
      stats: MasterDataStatsDto;
      health: HealthCheckDto;
      dropdowns: DropdownValuesDto;
    }> {
      const [stats, health, dropdowns] = await Promise.all([
        this.getMasterDataStats(),
        this.healthCheck(),
        this.getDropdownValues()
      ]);
  
      return { stats, health, dropdowns };
    }
  
    /**
     * Download export data as file
     */
    async downloadExportData(filename?: string): Promise<void> {
      try {
        const exportData = await this.exportMasterData();
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
          type: 'application/json' 
        });
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `master-data-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading export data:', error);
        throw error;
      }
    }
  
    /**
     * Format currency for display
     */
    static formatCurrency(amount: number): string {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    }
  
    /**
     * Format number with Indian locale
     */
    static formatNumber(number: number): string {
      return new Intl.NumberFormat('en-IN').format(number);
    }
  
    /**
     * Format percentage
     */
    static formatPercentage(value: number, decimals: number = 1): string {
      return `${value.toFixed(decimals)}%`;
    }
  }
  
  // Create singleton instance
  export const masterDataAPI = new MasterDataAPIService();
  
  // Export individual methods for convenience
  export const {
    getDistricts,
    getDistrictsWithStats,
    getDistrictById,
    getMandals,
    getMandalsByDistrict,
    getMandalsWithStats,
    getWorkTypes,
    getWorkTypeCategories,
    getContractors,
    getContractorsWithStats,
    getContractorById,
    searchMasterData,
    validateDistrictMandalCombination,
    validateContractorActive,
    getDropdownValues,
    getMasterDataStats,
    exportMasterData,
    healthCheck,
    seedMasterData,
    seedMandalData,
    downloadExportData
  } = masterDataAPI;